import type { Session } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useMemo, useState } from 'react'
import { AUTH_DISABLED, supabase } from './supabase'

type AuthState = {
  session: Session | null
  email: string | null
  /** Entitlement, not authentication. A signed-in user without this sees /upgrade. */
  subscribed: boolean
  loading: boolean
  signIn: (email: string, password: string) => Promise<void>
  signUp: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const Ctx = createContext<AuthState | null>(null)

function isSubscribed(session: Session | null): boolean {
  // Mirrors the backend, which reads the same claim and also defaults to false.
  // Defaulting to false means a missing claim denies access rather than
  // granting it — the paywall fails closed.
  const meta = session?.user?.app_metadata as
    | { subscription_active?: boolean }
    | undefined
  return meta?.subscription_active === true
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(!AUTH_DISABLED)

  useEffect(() => {
    if (AUTH_DISABLED) return
    if (!supabase) {
      // Auth is on but unconfigured. Stop loading and stay signed out, so the
      // gate redirects to a login page that can explain itself. Leaving
      // `loading` true here left the app on a spinner forever -- failing closed,
      // but invisibly, which is its own kind of broken.
      setLoading(false)
      return
    }
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => sub.subscription.unsubscribe()
  }, [])

  const value = useMemo<AuthState>(
    () => ({
      session,
      email: AUTH_DISABLED ? 'dev@localhost' : (session?.user?.email ?? null),
      subscribed: AUTH_DISABLED ? true : isSubscribed(session),
      loading,
      async signIn(email, password) {
        if (!supabase) throw new Error('Auth is not configured')
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw new Error(error.message)
      },
      async signUp(email, password) {
        if (!supabase) throw new Error('Auth is not configured')
        const { error } = await supabase.auth.signUp({ email, password })
        if (error) throw new Error(error.message)
      },
      async signOut() {
        await supabase?.auth.signOut()
        setSession(null)
      },
    }),
    [session, loading],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** True when there is a usable session (or auth is disabled for local dev). */
export function useIsAuthenticated() {
  const { session } = useAuth()
  return AUTH_DISABLED || Boolean(session)
}
